import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import * as XLSX from 'xlsx';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { AccountingReportsService, ReportDiagnostic } from '../../accounting-reports.service';
import { Party } from '../../../party/party.model';
import { PartyService } from '../../../party/party.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { ReportEmailComponent } from '../shared/report-email.component';
import { closeFinancialReportPdf, downloadFinancialReportPdf, openFinancialReportPdf } from '../shared/financial-report-pdf';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';
import {
    SalesRegisterFilter,
    SalesRegisterPageResponse,
    SalesRegisterRow,
    SalesRegisterSummary
} from './sales-register.model';

@Component({
    selector: 'app-sales-register',
    templateUrl: './sales-register.component.html',
    styleUrls: ['./sales-register.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        ButtonModule,
        CardModule,
        DatePickerModule,
        IftaLabelModule,
        InputTextModule,
        MultiSelectModule,
        SelectModule,
        ProgressBarModule,
        TableModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        PageDataPanelComponent,
        ReportEmailComponent
    ],
    providers: [
        AccountingReportsService,
        CurrencyPipe,
        MessageService,
        PartyService,
        SessionStorageService
    ]
})
export class SalesRegisterComponent implements OnInit {
    readonly reportActions = new PendingActionTracker<string>();
    rows: SalesRegisterRow[] = [];
    summary: SalesRegisterSummary = this.emptySummary();
    diagnostics: ReportDiagnostic[] = [];
    customers: Party[] = [];

    selectedCustomers: Party[] = [];

    startDate: Date;
    endDate: Date;
    invoiceNumber = '';
    shipmentNumber = '';
    deliveryNumber = '';
    selectedLogisticsProjectId?: number | null;
    selectedInvoiceTypes: string[] = [];
    selectedZatcaStatuses: string[] = [];

    readonly invoiceTypeOptions = ['SHIPMENT', 'ORDER', 'CLEARANCE', 'STORAGE'];
    readonly zatcaStatusOptions = ['PENDING', 'CLEARED', 'REPORTED', 'FAILED', 'NOT_APPLICABLE'];

    isLoading = false;
    totalRecords = 0;
    first = 0;
    rowsPerPage = 50;
    expandedRows: Record<string, boolean> = {};
    private requestNumber = 0;

    constructor(
        private accountingReportsService: AccountingReportsService,
        private sessionStorageService: SessionStorageService,
        private partyService: PartyService,
        private messageService: MessageService,
        private currencyPipe: CurrencyPipe,
        private router: Router
    ) { }

    ngOnInit(): void {
        const today = new Date();
        this.startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        this.endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        this.loadCustomers();

        this.loadSalesRegister({ first: 0, rows: this.rowsPerPage });
    }

    loadSalesRegister(event: TableLazyLoadEvent | Record<string, never>): void {
        const requestNumber = ++this.requestNumber;
        if (!this.startDate || !this.endDate) {
            this.messageService.add({ severity: 'warn', summary: 'Missing dates', detail: 'Select both start and end dates.' });
            return;
        }
        const first = Number((event as TableLazyLoadEvent).first ?? this.first ?? 0);
        const size = Number((event as TableLazyLoadEvent).rows ?? this.rowsPerPage);
        const page = Math.floor(first / size);
        this.first = first;
        this.rowsPerPage = size;
        this.isLoading = true;

        this.accountingReportsService.getSalesRegisterV2(this.companyId(), page, size, this.buildFilter())
            .subscribe({
                next: response => {
                    if (requestNumber === this.requestNumber) this.applyResponse(response);
                },
                error: () => {
                    if (requestNumber !== this.requestNumber) return;
                    this.isLoading = false;
                    this.rows = [];
                    this.summary = this.emptySummary();
                    this.diagnostics = [];
                    this.totalRecords = 0;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load Sales Register.' });
                }
            });
    }

    applyFilters(): void {
        this.first = 0;
        this.loadSalesRegister({ first: 0, rows: this.rowsPerPage });
    }

    clearFilters(): void {
        const today = new Date();
        this.startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        this.endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        this.selectedCustomers = [];
        this.invoiceNumber = '';
        this.shipmentNumber = '';
        this.deliveryNumber = '';
        this.selectedLogisticsProjectId = null;
        this.selectedInvoiceTypes = [];
        this.selectedZatcaStatuses = [];
        this.applyFilters();
    }

    exportExcel(): void {
        this.isLoading = true;
        this.accountingReportsService.getSalesRegisterAllV2(this.companyId(), this.buildFilter())
            .subscribe({
                next: response => {
                    this.isLoading = false;
                    const exportRows = this.flattenRows(response.content ?? []);
                    if (exportRows.length === 0) {
                        this.messageService.add({ severity: 'warn', summary: 'No data', detail: 'No data to export.' });
                        return;
                    }
                    const worksheet = XLSX.utils.json_to_sheet(exportRows);
                    worksheet['!cols'] = [
                        { wch: 12 }, { wch: 34 }, { wch: 18 }, { wch: 18 }, { wch: 16 },
                        { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 },
                        { wch: 12 }, { wch: 16 }, { wch: 22 }, { wch: 22 }, { wch: 18 }
                    ];
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Register');
                    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                    this.saveAsExcelFile(buffer, 'SalesRegister');
                    this.messageService.add({ severity: 'success', summary: 'Exported', detail: 'Sales Register exported.' });
                },
                error: () => {
                    this.isLoading = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to export Sales Register.' });
                }
            });
    }

    printReport(download = false): void {
        const action = download ? 'download' : 'print';
        if (!this.reportActions.begin(action)) return;
        const target = download ? null : window.open('', '_blank');
        this.accountingReportsService.printFinanceReport('sales-register', this.companyId(), {
            salesRegister: this.buildFilter()
        }).pipe(finalize(() => this.reportActions.end(action))).subscribe({
            next: blob => download ? downloadFinancialReportPdf(blob, 'sales-register') : openFinancialReportPdf(blob, target),
            error: () => {
                closeFinancialReportPdf(target);
                this.messageService.add({ severity: 'error', summary: 'Print unavailable', detail: 'Failed to generate the Sales Register PDF.' });
            }
        });
    }

    openInvoice(row: SalesRegisterRow): void {
        this.router.navigate(['/accounts/enterprise/receivables/generated-invoices/view', row.invoiceId]);
    }

    currencyCode(): string {
        return this.summary.currencyCode || this.rows[0]?.currencyCode || 'SAR';
    }

    costSeverity(source?: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (!source || source === 'UNMATCHED') return 'warn';
        if (source.includes('ALLOCATED')) return 'info';
        if (source === 'ACTUAL_BILL') return 'success';
        return 'secondary';
    }

    private loadCustomers(): void {
        this.partyService.getParties(this.companyId(), ['Company', 'Customer'], '', 0, 1000)
            .subscribe({
                next: page => this.customers = page.content ?? [],
                error: () => this.customers = []
            });
    }

    private applyResponse(response: SalesRegisterPageResponse): void {
        this.rows = response.content ?? [];
        this.summary = response.summary ?? this.emptySummary();
        this.diagnostics = response.diagnostics ?? [];
        this.totalRecords = response.totalElements ?? 0;
        this.expandedRows = {};
        this.isLoading = false;
    }

    private buildFilter(): SalesRegisterFilter {
        return {
            startDate: this.startOfDay(this.startDate).getTime(),
            endDate: this.endOfDay(this.endDate).getTime(),
            customerPartyKeys: this.selectedCustomers
                .filter(customer => customer.id?.entityId != null && customer.id?.entityType)
                .map(customer => `${customer.id.entityType.toUpperCase()}:${customer.id.entityId}`),
            invoiceNumber: this.trimOrNull(this.invoiceNumber),
            shipmentNumber: this.trimOrNull(this.shipmentNumber),
            deliveryNumber: this.trimOrNull(this.deliveryNumber),
            invoiceTypes: this.selectedInvoiceTypes,
            zatcaStatuses: this.selectedZatcaStatuses,
            logisticsProjectId: this.selectedLogisticsProjectId || undefined
        };
    }

    private flattenRows(rows: SalesRegisterRow[]): Record<string, string | number>[] {
        const exportRows: Record<string, string | number>[] = [];
        rows.forEach(row => {
            exportRows.push({
                Date: this.formatDate(row.invoiceDate),
                Particulars: row.customerName || '',
                'Vch No': row.invoiceNumber,
                'Gross Total': this.number(row.grossTotal),
                Sales: this.number(row.salesAmount),
                VAT: this.number(row.vatAmount),
                'Cost of Sales': this.number(row.costOfSales),
                'Item Profit': this.number(row.itemProfit),
                'Gross Profit': this.number(row.grossProfit),
                'GP %': this.number(row.grossProfitPercent),
                Shipment: row.shipmentNumber || '',
                Type: row.invoiceType || '',
                'Cost Source': '',
                'Line Description': '',
                Currency: row.currencyCode || ''
            });
            row.lines?.forEach(line => {
                exportRows.push({
                    Date: '',
                    Particulars: `  ${line.itemDescription || line.itemNumber || ''}`,
                    'Vch No': '',
                    'Gross Total': this.number(line.grossTotal),
                    Sales: this.number(line.salesAmount),
                    VAT: this.number(line.vatAmount),
                    'Cost of Sales': this.number(line.costOfSales),
                    'Item Profit': this.number(line.itemProfit),
                    'Gross Profit': this.number(line.grossProfit),
                    'GP %': this.number(line.grossProfitPercent),
                    Shipment: line.headerReferenceNumber || '',
                    Type: line.detailReferenceNumber || '',
                    'Cost Source': line.costSource || '',
                    'Line Description': line.itemDescription || '',
                    Currency: row.currencyCode || ''
                });
            });
        });
        return exportRows;
    }

    private openPrintWindow(response: SalesRegisterPageResponse): void {
        const rows = response.content ?? [];
        if (rows.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No data', detail: 'There is no data to print.' });
            return;
        }
        const company = this.sessionStorageService.getCompany();
        const currency = response.summary?.currencyCode || this.currencyCode();
        const money = (value: number) => this.currencyPipe.transform(value || 0, currency, 'symbol', '1.2-2') || '';
        const styles = `
            <style>
                body { font-family: Arial, sans-serif; font-size: 11px; color: #111827; }
                h1 { font-size: 18px; margin: 0 0 4px; }
                .meta { margin-bottom: 12px; color: #4b5563; }
                table { width: 100%; border-collapse: collapse; }
                th, td { border: 1px solid #d1d5db; padding: 5px; }
                th { background: #f3f4f6; text-align: left; }
                .number { text-align: right; }
                .line { color: #374151; font-style: italic; }
                .total { font-weight: 700; background: #f9fafb; }
            </style>`;
        const bodyRows = rows.map(row => `
            <tr>
                <td>${this.escapeHtml(this.formatDate(row.invoiceDate))}</td>
                <td>${this.escapeHtml(row.customerName || '')}</td>
                <td>${this.escapeHtml(row.invoiceNumber)}</td>
                <td class="number">${money(row.grossTotal)}</td>
                <td class="number">${money(row.salesAmount)}</td>
                <td class="number">${money(row.vatAmount)}</td>
                <td class="number">${money(row.costOfSales)}</td>
                <td class="number">${money(row.grossProfit)}</td>
                <td class="number">${this.number(row.grossProfitPercent).toFixed(2)}%</td>
            </tr>
            ${(row.lines || []).map(line => `
                <tr class="line">
                    <td></td>
                    <td>${this.escapeHtml(line.headerReferenceNumber || '')} / ${this.escapeHtml(line.detailReferenceNumber || '')}</td>
                    <td>${this.escapeHtml(line.itemNumber || '')}</td>
                    <td class="number">${money(line.grossTotal)}</td>
                    <td class="number">${money(line.salesAmount)}</td>
                    <td class="number">${money(line.vatAmount)}</td>
                    <td class="number">${money(line.costOfSales)}</td>
                    <td class="number">${money(line.grossProfit)}</td>
                    <td class="number">${this.number(line.grossProfitPercent).toFixed(2)}%</td>
                </tr>`).join('')}`).join('');
        const html = `
            <html>
                <head><title>Sales Register</title>${styles}</head>
                <body>
                    <h1>${this.escapeHtml(company?.name || 'Sales Register')}</h1>
                    <div class="meta">Sales Register | ${this.formatDate(this.startDate)} to ${this.formatDate(this.endDate)}</div>
                    <table>
                        <thead>
                            <tr>
                                <th>Date</th><th>Particulars</th><th>Vch No</th><th class="number">Gross Total</th>
                                <th class="number">Sales</th><th class="number">VAT</th><th class="number">Cost of Sales</th>
                                <th class="number">Gross Profit</th><th class="number">GP %</th>
                            </tr>
                        </thead>
                        <tbody>${bodyRows}</tbody>
                        <tfoot>
                            <tr class="total">
                                <td colspan="3" class="number">Grand Total</td>
                                <td class="number">${money(response.summary?.grossTotal || 0)}</td>
                                <td class="number">${money(response.summary?.salesAmount || 0)}</td>
                                <td class="number">${money(response.summary?.vatAmount || 0)}</td>
                                <td class="number">${money(response.summary?.costOfSales || 0)}</td>
                                <td class="number">${money(response.summary?.grossProfit || 0)}</td>
                                <td class="number">${this.number(response.summary?.grossProfitPercent).toFixed(2)}%</td>
                            </tr>
                        </tfoot>
                    </table>
                </body>
            </html>`;
        const printWindow = window.open('', '_blank', 'height=800,width=1100');
        if (printWindow) {
            printWindow.document.write(html);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 500);
        }
    }

    private saveAsExcelFile(buffer: ArrayBuffer, fileName: string): void {
        const data = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        const downloadLink = document.createElement('a');
        const url = URL.createObjectURL(data);
        downloadLink.href = url;
        downloadLink.download = `${fileName}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
    }

    private emptySummary(): SalesRegisterSummary {
        return {
            grossTotal: 0,
            salesAmount: 0,
            vatAmount: 0,
            costOfSales: 0,
            itemProfit: 0,
            grossProfit: 0,
            grossProfitPercent: 0,
            currencyCode: 'SAR',
            recordCount: 0
        };
    }

    private startOfDay(value: Date): Date {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0);
    }

    private endOfDay(value: Date): Date {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 23, 59, 59, 999);
    }

    private trimOrNull(value: string): string | null {
        const trimmed = (value || '').trim();
        return trimmed.length ? trimmed : null;
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }

    private number(value: number | string | null | undefined): number {
        return Number(value ?? 0) || 0;
    }

    private formatDate(value: string | Date): string {
        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }
}
